
import { useEffect, useRef, useCallback } from 'react';
import { Call } from '@twilio/voice-sdk';
import { useToast } from '@/hooks/use-toast';
import { getCurrentCall } from '@/services/twilio/voiceService';
import type { CallState, UpdateCallStateAction } from '@/hooks/useTwilioCallState';
import type { TwilioCallTimers } from '@/hooks/useTwilioCallTimers';
import type { UseTwilioCallLogsReturn } from '@/hooks/useTwilioCallLogs';
import { processCallStatusChange } from './event-handlers/callStatusChangeLogic';

interface UseTwilioEventListenersProps {
  localCallSidRef: React.MutableRefObject<string | null>;
  callLogIdRef: React.MutableRefObject<number | null>;
  callState: CallState; 
  callStateActions: {
    updateCallState: UpdateCallStateAction;
    endCallState: () => void;
    resetCallState: () => void;
  };
  callTimers: Pick<TwilioCallTimers, 'startDurationTimer' | 'stopPolling'>;
  callLogActions: Pick<UseTwilioCallLogsReturn, 'finalizeCallLogInDb' | 'updateCallProgress' | 'clearSession' | 'hasActiveSession'>;
}

export const useTwilioEventListeners = ({
  localCallSidRef,
  callLogIdRef,
  callState, 
  callStateActions,
  callTimers,
  callLogActions,
}: UseTwilioEventListenersProps) => {
  const { toast } = useToast();
  const { endCallState, resetCallState, updateCallState } = callStateActions; 
  const { stopPolling } = callTimers; 
  const { finalizeCallLogInDb, hasActiveSession } = callLogActions;
  
  // Use refs to prevent effect re-runs
  const currentCallRef = useRef<Call | null>(null);
  const isCleaningUpRef = useRef(false);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Stable callback for handling status changes
  const handleStatusChange = useCallback((sdkStatus: Call.State | string, twilioCall: Call) => {
    console.log(`[EventListener] 📊 Processing status change: ${sdkStatus} for SID: ${twilioCall.parameters.CallSid}`);
    
    processCallStatusChange({
      newStatus: sdkStatus as string,
      twilioCall,
      localCallSidRef,
      callLogIdRef,
      currentDuration: callState.duration, 
      callStateActions, 
      callTimers,       
      callLogActions,   
    });
  }, [callState.duration, callStateActions, callTimers, callLogActions, localCallSidRef, callLogIdRef]);

  // Stable cleanup function
  const performCleanup = useCallback(async (reason: string) => {
    if (isCleaningUpRef.current) {
      console.log('[EventListener] ⚠️ Cleanup already in progress, skipping');
      return;
    }
    
    isCleaningUpRef.current = true;
    console.log(`[EventListener] 🧹 Performing cleanup - Reason: ${reason}`);
    
    try {
      // Clear interval first
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }

      // Remove call listeners if we have a current call
      if (currentCallRef.current) {
        console.log(`[EventListener] 🧹 Removing listeners for call SID: ${currentCallRef.current.parameters.CallSid}`);
        currentCallRef.current.removeAllListeners();
        currentCallRef.current = null;
      }

      // Finalize call log if needed
      if (callLogIdRef.current && hasActiveSession() && callState.callInProgress) {
        console.log(`[EventListener] 📋 Finalizing call log ${callLogIdRef.current} during cleanup`);
        await finalizeCallLogInDb('failed', `Call ended unexpectedly: ${reason}`, callState.duration);
      }

      // End call state (preserve data) instead of full reset
      stopPolling();
      endCallState();
      localCallSidRef.current = null;
      callLogIdRef.current = null;
    } catch (error) {
      console.error('[EventListener] ❌ Error during cleanup:', error);
    } finally {
      isCleaningUpRef.current = false;
    }
  }, [callLogIdRef, hasActiveSession, callState.callInProgress, callState.duration, finalizeCallLogInDb, stopPolling, endCallState, localCallSidRef]);

  // Stable function to attach event listeners to a call
  const attachCallListeners = useCallback((twilioCall: Call) => {
    const callSid = twilioCall.parameters.CallSid;
    console.log(`[EventListener] 🔗 Attaching listeners to call SID: ${callSid}`);

    // Store reference
    currentCallRef.current = twilioCall;
    localCallSidRef.current = callSid;

    // Update state with initial call info
    updateCallState({
      callSid: callSid,
      callStatus: twilioCall.status(),
      callInProgress: true
    });

    // Handle initial status
    handleStatusChange(twilioCall.status(), twilioCall);

    // Register comprehensive event listeners
    twilioCall.on('accept', () => {
      console.log(`[EventListener] ✅ Call accepted - SID: ${callSid}`);
      handleStatusChange(Call.State.Open, twilioCall);
    });

    twilioCall.on('disconnect', (disconnectCall) => {
      console.log(`[EventListener] 📴 Call disconnected - SID: ${callSid}`);
      console.log(`[EventListener] 📴 Disconnect call object:`, disconnectCall);
      handleStatusChange(Call.State.Closed, twilioCall);
    });

    twilioCall.on('cancel', () => {
      console.log(`[EventListener] ❌ Call cancelled - SID: ${callSid}`);
      handleStatusChange('canceled', twilioCall);
    });

    twilioCall.on('reject', () => {
      console.log(`[EventListener] ❌ Call rejected - SID: ${callSid}`);
      handleStatusChange('busy', twilioCall);
    });
    
    twilioCall.on('status', (sdkStatus: Call.State) => { 
      console.log(`[EventListener] 📊 Status event: ${sdkStatus} for SID: ${callSid}`);
      handleStatusChange(sdkStatus, twilioCall);
    });

    twilioCall.on('error', (error: any) => { 
      console.error(`[EventListener] ❌ Call Error for SID ${callSid}:`, error);
      toast({ 
        title: "Call Error", 
        description: error.message || `Twilio error code: ${error.code}`, 
        variant: "destructive" 
      });
      handleStatusChange('failed', twilioCall);
    });
  }, [handleStatusChange, localCallSidRef, updateCallState, toast]);

  useEffect(() => {
    console.log('[EventListener] 🚀 Initializing event listeners');
    
    // Check for existing call on mount
    const existingCall = getCurrentCall();
    if (existingCall && existingCall.parameters.CallSid !== localCallSidRef.current) {
      console.log('[EventListener] 📞 Found existing call on mount:', existingCall.parameters.CallSid);
      attachCallListeners(existingCall);
    }

    // Set up call monitoring interval with proper reference tracking
    checkIntervalRef.current = setInterval(() => {
      const currentTwilioCall = getCurrentCall();
      
      if (currentTwilioCall) {
        const currentSid = currentTwilioCall.parameters.CallSid;
        
        // If we have a new call or lost track of the current one
        if (currentSid !== localCallSidRef.current) {
          console.log('[EventListener] 🔄 Detected new call or re-attaching:', currentSid);
          attachCallListeners(currentTwilioCall);
        }
      } else if (localCallSidRef.current && !isCleaningUpRef.current) {
        // Call disappeared without proper cleanup
        console.warn(`[EventListener] 👻 Call SID ${localCallSidRef.current} disappeared without disconnect event`);
        performCleanup('Call disappeared unexpectedly');
      }
    }, 3000); // Increased interval to reduce overhead

    // Cleanup function
    return () => {
      console.log('[EventListener] 🧹 Component unmounting, performing cleanup');
      performCleanup('Component unmounting');
    };
  }, []); // Empty dependency array to prevent re-runs

  // Separate effect for handling external state changes
  useEffect(() => {
    // If call is no longer in progress but we still have a call reference, clean up
    if (!callState.callInProgress && currentCallRef.current && !isCleaningUpRef.current) {
      console.log('[EventListener] 🔄 Call no longer in progress, cleaning up call reference');
      if (currentCallRef.current) {
        currentCallRef.current.removeAllListeners();
        currentCallRef.current = null;
      }
    }
  }, [callState.callInProgress]);
};
